export const MIN_FACTOR = 1;
export const MAX_FACTOR = 10;

export type MultiplicationFactId = `mul.${number}x${number}`;
export type MultiplicationFamilyId = `mul.family.${number}x${number}`;

export interface MultiplicationFact {
  readonly id: MultiplicationFactId;
  readonly familyId: MultiplicationFamilyId;
  readonly left: number;
  readonly right: number;
  readonly answer: number;
}

export interface MultiplicationFamily {
  readonly id: MultiplicationFamilyId;
  readonly low: number;
  readonly high: number;
  readonly factIds: readonly MultiplicationFactId[];
}

export interface MultiplicationCatalog {
  readonly version: string;
  readonly facts: readonly MultiplicationFact[];
  readonly families: readonly MultiplicationFamily[];
  readonly factById: ReadonlyMap<MultiplicationFactId, MultiplicationFact>;
  readonly familyById: ReadonlyMap<MultiplicationFamilyId, MultiplicationFamily>;
}

export function multiplicationFactId(left: number, right: number): MultiplicationFactId {
  return `mul.${left}x${right}`;
}

export function multiplicationFamilyId(left: number, right: number): MultiplicationFamilyId {
  const low = Math.min(left, right);
  const high = Math.max(left, right);
  return `mul.family.${low}x${high}`;
}

export function buildMultiplicationCatalog(): MultiplicationCatalog {
  const facts: MultiplicationFact[] = [];
  const familyMembers = new Map<MultiplicationFamilyId, MultiplicationFactId[]>();

  for (let left = MIN_FACTOR; left <= MAX_FACTOR; left += 1) {
    for (let right = MIN_FACTOR; right <= MAX_FACTOR; right += 1) {
      const id = multiplicationFactId(left, right);
      const familyId = multiplicationFamilyId(left, right);
      facts.push({ id, familyId, left, right, answer: left * right });
      const members = familyMembers.get(familyId) ?? [];
      members.push(id);
      familyMembers.set(familyId, members);
    }
  }

  const families = [...familyMembers.entries()].map(([id, factIds]) => {
    const [firstFactId] = factIds;
    const fact = facts.find((candidate) => candidate.id === firstFactId)!;
    return {
      id,
      low: Math.min(fact.left, fact.right),
      high: Math.max(fact.left, fact.right),
      factIds,
    } satisfies MultiplicationFamily;
  });

  return {
    version: "multiplication-1-to-10-v1",
    facts,
    families,
    factById: new Map(facts.map((fact) => [fact.id, fact])),
    familyById: new Map(families.map((family) => [family.id, family])),
  };
}

export const MULTIPLICATION_CATALOG = buildMultiplicationCatalog();
